import axios, { AxiosRequestConfig } from 'axios';

/**
 * Administration (13/10/2026) — deux lectures, réservées aux adresses de
 * `ADMIN_EMAILS` côté serveur. La porte est opaque : un 404 veut dire « pas
 * administrateur », jamais une erreur à afficher. Les formes miroitent
 * `api/src/services/AdminUsageService.ts`.
 */

export type AdminAcces = 'inconnu' | 'admin' | 'non';
export type AdminTri = 'questions' | 'fichiers' | 'notes' | 'activite';
export type AdminForfaitTier = 'free' | 'plus';

export interface AdminGlobal {
    comptes: { total: number; aujourdhui: number; sept_jours: number; trente_jours: number };
    notes: { total: number; modifiees_sept_jours: number };
    fichiers: { nombre: number; octets: number };
    textesExtraits: { nombre: number; caracteres: number };
    chunks: { nombre: number; octetsEstimes: number };
    questions: { periode: string; mois: number; aujourdhui: number };
    recherchesAujourdhui: number;
    lotsEnCours: number;
    collaborationsActives: number;
    notificationsNonLues: number;
}

export interface AdminCoupeCircuit {
    geste: string;
    label: string;
    limit: number;
    used: number;
    resetsOn: string;
}

export interface AdminForfaitDefinition {
    tier: AdminForfaitTier;
    label: string;
    questionsPerMonth: number;
    questionsPerMinute: number;
    searchesPerMinute: number;
    attachmentsPerQuestion: number;
    attachmentChars: number;
    bulkMaxTargets: number;
    maxFiles: number;
    maxStorageBytes: number;
    maxFileBytes: number;
    maxCollaborators: number;
}

export type AdminOrganisationTier = 'decouverte' | 'plus';

/** La grille d'un palier d'organisation (C26 SP5) : celle des personnes, plus le plafond de membres. */
export interface AdminForfaitOrganisationDefinition extends Omit<AdminForfaitDefinition, 'tier'> {
    tier: AdminOrganisationTier;
    maxMembres: number;
}

export interface AdminForfaits {
    grille: Record<AdminForfaitTier, AdminForfaitDefinition>;
    repartition: Record<AdminForfaitTier, number>;
    /** C26 SP5 : absents d'une API antérieure — l'écran les traite comme vides. */
    grilleOrganisations?: Record<AdminOrganisationTier, AdminForfaitOrganisationDefinition>;
    repartitionOrganisations?: Record<AdminOrganisationTier, number>;
}

/** Une organisation dans la grille de la plateforme (C26 SP5) : des nombres, jamais une clé ni un membre nommé. */
export interface AdminOrganisation {
    id: string;
    nom: string;
    slug: string;
    forfait: { tier: AdminOrganisationTier; label: string };
    source: 'manual' | 'stripe' | 'aucune';
    creeLe: string | null;
    membres: number;
    documents: number;
    octets: number;
    travaux: number;
    chunks: number;
    questionsMois: number;
    cleProprePresente: boolean;
}

export interface AdminUtilisateur {
    userId: string;
    displayName: string;
    email: string;
    forfait: { tier: AdminForfaitTier; label: string };
    creeLe: string | null;
    derniereActivite: string | null;
    questionsMois: number;
    questionsAujourdhui: number;
    recherchesAujourdhui: number;
    notes: number;
    fichiers: number;
    octets: number;
    chunks: number;
    cleProprePresente: boolean;
}

export interface AdminUsage {
    generatedAt: string;
    periode: string;
    global: AdminGlobal;
    coupeCircuits: AdminCoupeCircuit[];
    forfaits: AdminForfaits;
    utilisateurs: AdminUtilisateur[];
    totalUtilisateurs: number;
    /** C26 SP5 : toutes les organisations, questions du mois ↓ ; absentes d'une API antérieure. */
    organisations?: AdminOrganisation[];
    totalOrganisations?: number;
    /** 28/10/2026 : absente d'une API antérieure ; `null` quand la mesure a échoué côté serveur. */
    premiersPas?: AdminPremiersPas | null;
}

/** 28/10/2026 : la liste « Premiers pas » mesurée sur tous les comptes — des nombres, jamais une personne. */
export type AdminPremiersPasLigne = 'document' | 'citation' | 'concept' | 'assistant' | 'partage' | 'noteReliee' | 'definitionLue';
export interface AdminPremiersPas {
    comptes: number;
    comptesRecents: number;
    joursRecents: number;
    lignes: Array<{ id: AdminPremiersPasLigne; libelle: string; palier: 1 | 2; comptes: number; comptesRecents: number }>;
    /** Comptes par nombre de lignes du premier palier faites, de 0 à 5. */
    progression: Array<{ faites: number; comptes: number; comptesRecents: number }>;
}

export interface AdminUsageParams {
    periode?: string;
    tri?: AdminTri;
    limite?: number;
}

const GENERIC = 'Les chiffres ne peuvent pas être lus pour l\'instant. Réessayez plus tard.';

const config = (accessToken: string, params?: Record<string, string | number>): AxiosRequestConfig => ({
    headers: { Authorization: `Bearer ${accessToken}`, AccessToken: accessToken, 'Content-Type': 'application/json' },
    ...(params && Object.keys(params).length > 0 ? { params } : {}),
});

function messageOf(error: any): string {
    const data = error?.response?.data;
    if (data?.message) return data.message;
    if (error?.response) return GENERIC;
    return 'Connexion impossible. Vérifiez votre réseau et réessayez.';
}

/** Les paramètres effectivement envoyés : rien d'indéfini, rien d'inutile. */
export function usageQuery(params: AdminUsageParams = {}): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    if (params.periode) out.periode = params.periode;
    if (params.tri) out.tri = params.tri;
    if (params.limite !== undefined) out.limite = params.limite;
    return out;
}

export const adminAPI = {
    /** 200 → `admin`, 404 → `non` (silencieux) ; toute autre erreur est levée. */
    acces: async (accessToken: string): Promise<Exclude<AdminAcces, 'inconnu'>> => {
        try {
            await axios.get('/api/gestion/acces', config(accessToken));
            return 'admin';
        } catch (error: any) {
            if (error?.response?.status === 404) return 'non';
            throw new Error(messageOf(error));
        }
    },

    usage: async (accessToken: string, params: AdminUsageParams = {}): Promise<AdminUsage> => {
        try {
            const response = await axios.get('/api/gestion/usage', config(accessToken, usageQuery(params)));
            return response.data.data as AdminUsage;
        } catch (error: any) {
            throw new Error(messageOf(error));
        }
    },
};
